import React from 'react';
import { Warning } from '@mui/icons-material';
import { FormInputSelect } from 'components/form';
import { ActionsMenu } from 'components/shared/Menu/ActionsMenu';
import { Button, ButtonGroup, Tooltip } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Header } from './Header';
import { Details } from './Details';
import { isDepartmentWithWarning } from '../lib';
import { StagesSkeleton } from './StagesSkeleton';
import { StyledContainer } from './StyledContainer';
import { VOStagesContainer } from './VOStagesContainer';
import { StagesCreateFallBack } from './StagesCreateFallBack';
import { EmptyConfigurationMessage } from './EmptyConfigurationMessage';

interface Props {
  voId: string;
  accountId: string;
  onSwitchToLegacyStages: VoidFunction;
}

export const VOStagesManager = ({ voId, accountId, onSwitchToLegacyStages }: Props) => (
  <VOStagesContainer id={voId} accountId={accountId}>
    {({
      actions,
      loading,
      templates,
      viewedStage,
      departments,
      selectedStage,
      stagesOptions,
      selectedDepartment,
      attributeRenderers,
    }) => (
      <StyledContainer>
        <RenderWithFallback isLoading={loading.genericLoading} fallBack={<StagesSkeleton />}>
          <Header
            departmentsSlot={
              <ButtonGroup>
                {departments.map(({ name, id }) => (
                  <Button
                    key={id}
                    startIcon={
                      <RenderWithCondition condition={isDepartmentWithWarning(templates, id)}>
                        <Tooltip arrow placement="top-start" title={<EmptyConfigurationMessage />}>
                          <Warning color="warning" />
                        </Tooltip>
                      </RenderWithCondition>
                    }
                    variant={selectedDepartment === id ? 'contained' : 'outlined'}
                    onClick={() => actions.onSelectDepartment(id)}
                  >
                    {name}
                  </Button>
                ))}
              </ButtonGroup>
            }
            actionsSlot={<ActionsMenu config={[{ label: 'Legacy Stages', action: onSwitchToLegacyStages }]} />}
          />

          <RenderWithCondition
            condition={!!templates[selectedDepartment!]?.stages}
            fallBack={<StagesCreateFallBack onCreateTemplate={actions.onCreateTemplate} />}
          >
            <Details
              loading={loading}
              viewedStage={viewedStage}
              activeStage={selectedStage!}
              onViewStage={actions.onViewStage}
              attributeRenderers={attributeRenderers}
              stages={templates[selectedDepartment!]?.stages}
              changeStageStatusSlot={
                <FormInputSelect
                  size="small"
                  label="Set stage"
                  value={selectedStage}
                  options={stagesOptions}
                  onChange={actions.onSelectStage}
                  isLoading={loading.isBPFsUpdating}
                />
              }
            />
          </RenderWithCondition>
        </RenderWithFallback>
      </StyledContainer>
    )}
  </VOStagesContainer>
);
